import { convertToTimezone, replaceTimezone } from "@/lib/dateUtils";

/**
 * The picker holds browser-LOCAL wall-clock components, so a stored absolute instant has to
 * be restamped both ways or a curator outside the venue's zone edits a shifted time.
 * `EventsEdit` does this inline with the event's own `timezone`; a performance has no
 * timezone of its own and `PerformanceVenueDbDto` carries none either (theater spec §3.6),
 * so the caller fetches the venue document and may still come back empty-handed.
 *
 * With no timezone, both directions are the identity: the value keeps browser-local
 * semantics, which is what the picker would have done anyway, and the page warns.
 *
 * `startDateForPicker` forks `prefillDatetimeForPicker` (`src/lib/dateUtils.ts`) rather than
 * delegating to it: the explore/Typesense projection can hand this page a unix-second number
 * (`toDate` below accepts `number | string | null | undefined`), and an unparseable value
 * must come back `undefined` rather than an Invalid Date — neither of which
 * `prefillDatetimeForPicker`'s plain-string signature needs to handle.
 */
function toDate(value: string | number | null | undefined): Date | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const date =
    typeof value === "number" ? new Date(value * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Stored instant → the ISO string the DateTimePicker should be seeded with. */
export function startDateForPicker(
  iso: string | number | null | undefined,
  timezone: string | null | undefined,
): string | undefined {
  const date = toDate(iso);
  if (!date) return undefined;
  if (!timezone) return date.toISOString();
  return convertToTimezone(date, timezone).toISOString();
}

/** Picker output → the absolute instant to PUT as `startDate`. */
export function startDateForSubmit(
  pickerValue: string | null | undefined,
  timezone: string | null | undefined,
): string | undefined {
  const date = toDate(pickerValue);
  if (!date) return undefined;
  if (!timezone) return date.toISOString();
  return replaceTimezone(date, timezone).toISOString();
}
